import React, { Fragment, useEffect, useState } from "react";
import ListSubheader from "@mui/material/ListSubheader";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import Collapse from "@mui/material/Collapse";
import { Link, Navigate, useNavigate } from "react-router-dom";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import { useTheme } from "@mui/material/styles";
import Divider from "@mui/material/Divider";
import { ReactComponent as DashboardIcon } from "../../components/nav-icons/Dashboard.svg";
import { ReactComponent as AssetsIcon } from "../../components/nav-icons/Asset.svg";
import { ReactComponent as OffsetIcon } from "../../components/nav-icons/offset.svg";
import { ReactComponent as AuditIcon } from "../../components/nav-icons/Audit.svg";
import { ReactComponent as TargetIcon } from "../../components/nav-icons/Target.svg";
import { ReactComponent as DataSourceIcon } from "../../components/nav-icons/Datasource.svg";
import { ReactComponent as HelpIcon } from "../../components/nav-icons/Help.svg";
import { ReactComponent as GMLogo } from "../../components/nav-icons/GMLogo.svg";
import { ReactComponent as LogIcon } from "../../components/nav-icons/Log.svg";
import { ReactComponent as HomeIcon } from "../../components/nav-icons/Home.svg";
import { ReactComponent as OrganisationIcon } from "../../components/nav-icons/Organisation.svg";
import { ReactComponent as ReportsIcon } from "../../components/nav-icons/Reports.svg";
import { ReactComponent as PartnersIcon } from "../../components/nav-icons/Partners.svg";
import { ReactComponent as SettingsIcon } from "../../components/nav-icons/Settings.svg";
import SvgIcon from "@mui/material/SvgIcon";
import "./SideNav.scss";
import KeyboardArrowRightRoundedIcon from "@mui/icons-material/KeyboardArrowRightRounded";
import KeyboardArrowLeftRoundedIcon from "@mui/icons-material/KeyboardArrowLeftRounded";
import { neutral } from "../../constants/colorConstants";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { checkPermissions, checkSubscriptionPermission } from "../../helper";
import SubscriptionUpgrade from "../../components/Subscription/SubscriptionUpgrade";
import SubscriptionRemainder from "../../components/Subscription/SubscriptionRemainder";
import UpgradeMessageDialog from "../../components/Subscription/UpgradeMessageDialog";
import { Hidden } from "@mui/material";
import { greenMirrorUrl } from "../../server-data";

const SideNav = () => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const styles = {
    section: {
      textDecoration: "none",
    },
    default: {
      textDecoration: "none",
    },
    navActive: {
      backgroundColor: `${theme.palette.primary} !important`,
      color: theme.palette.grey[50],
    },
  };
  const navigate = useNavigate();
  let userRole = JSON.parse(localStorage.getItem("user") || "{}").role;
  let token = localStorage.getItem("token") || "";
  if (token && !userRole) {
    userRole = JSON.parse(atob(token?.split(".")[1])).iss.role;
  }

  // RBAC and plan permissions
  const rbacPermissions = useSelector(
    (state: any) => state.auth.userPermissions
  );
  const planPermissions = useSelector(
    (state: any) => state.sub.subscriptionPermissions
  );

  const subscriptionApiCompleted = useSelector(
    (state: any) => state.sub.subscriptionApiCompleted
  );

  // Conponent states
  const [openSettings, setOpenSettings] = useState(false);
  const [sideNavExpand, setSideNavExpand] = useState(true);
  const [showUpgradeButton, setShowUpgradeButton] = useState(false);
  const [openUpgradeDialog, setOpenUpgradeDialog] = useState(false);

  // Sidenav list

  const getHomeNavItems = () => {
    const homeNavItems: any = [];
    // checkPermissions([""], rbacPermissions) &&
    userRole !== 7 &&
      homeNavItems.push({
        listItem: "Home",
        icon: HomeIcon,
        path: "/home",
        children: [],
        isOpen: false,
        planPermission: "DASHBOARD",
        activePaths: new Set(["/home"]),
      });
    return homeNavItems;
  };

  const getAnalyticsNavItems = () => {
    const analyticsNavItems = [];
    analyticsNavItems.push({
      listItem: "Dashboard",
      path: "/dashboard",
      children: [],
      icon: DashboardIcon,
      isOpen: false,
      planPermission: "DASHBOARD",
      activePaths: new Set(["/dashboard"]),
    });
    checkPermissions(["READ_REPORT"], rbacPermissions) &&
      analyticsNavItems.push({
        listItem: "Reports",
        path: "/reports",
        icon: ReportsIcon,
        isOpen: false,
        children: [],
        planPermission: "REPORTS",
        activePaths: new Set(["/reports"]),
      });
    return analyticsNavItems;
  };

  const getCompanyNavItems = () => {
    const companyNavItems = [];
    const organisationNavItems = [];
    checkPermissions(["READ_ALL_FACILITIES"], rbacPermissions) &&
      organisationNavItems.push({
        name: "Facility",
        path: "/facility",
        planPermission: "FACILITY",
        activePaths: new Set([
          "/organisation/facility",
          "/organisation/facilityconfigurations",
        ]),
      });
    checkPermissions(["READ_EMPLOYEE_LIST"], rbacPermissions) &&
      organisationNavItems.push({
        name: "Employees",
        path: "/employee",
        planPermission: "USER",
        activePaths: new Set([
          "/organisation/employee/employee-list",
          "/organisation/employee/edit-employee",
        ]),
      });
    checkPermissions(["READ_DISCLOSURE"], rbacPermissions) &&
      organisationNavItems.push({
        name: "Disclosures",
        path: "/disclosures",
        planPermission: "DISCLOSURE",
        activePaths: new Set(["/organisation/disclosures"]),
      });
    organisationNavItems.length &&
      companyNavItems.push({
        listItem: "Organisation",
        path: "/organisation",
        icon: OrganisationIcon,
        children: organisationNavItems,
        isOpen: false,
        activePaths: new Set(["/organisation"]),
      });
    companyNavItems.push({
      listItem: "Target",
      path: "/targets",
      icon: TargetIcon,
      children: [],
      isOpen: false,
      planPermission: "TARGETS",
      activePaths: new Set(["/targets", "/target"]),
    });
    checkPermissions(["READ_EMISSION_FACTOR"], rbacPermissions) &&
      companyNavItems.push({
        listItem: "Data Sources",
        path: "/datasource",
        icon: DataSourceIcon,
        children: [
          {
            name: "Standard Emission Factors",
            path: "/standardemissionfactor",
            planPermission: "EMISSIONS",
            activePaths: new Set(["/datasource/standardemissionfactor"]),
          },
          {
            name: "Custom Emission Factors",
            path: "/customemissionfactor",
            planPermission: "CUSTOM_EMISSIONS",
            activePaths: new Set([
              "/datasource/customemissionfactor",
              "/datasource/customemission",
            ]),
          },
        ],
        isOpen: false,
      });
    checkPermissions(["READ_AUDIT_REQUESTS_LIST"], rbacPermissions) &&
      companyNavItems.push({
        listItem: "Audit",
        path: "/audits",
        icon: AuditIcon,
        children: [],
        isOpen: false,
        planPermission: "AUDIT",
        activePaths: new Set(["/audits", "/audit"]),
      });
    return companyNavItems;
  };
  const getFacilityNavItems = () => {
    const facilityNavItems = [];
    checkPermissions(["READ_EMISSIONS"], rbacPermissions) &&
      facilityNavItems.push({
        listItem: "Log",
        path: "/log",
        icon: LogIcon,
        children: [
          {
            name: "Emissions",
            path: "/Emissions",
            active: false,
            planPermission: "EMISSIONS",
            activePaths: new Set([
              "/log/Emissions",
              "/log/logemissions",
              "/log/emissions",
            ]),
          },
        ],
        isOpen: false,
        activePaths: new Set(["/log"]),
      });
    checkPermissions(["READ_ASSET_LIST"], rbacPermissions) &&
      facilityNavItems.push({
        listItem: "Assets",
        path: "/assets",
        icon: AssetsIcon,
        children: [],
        isOpen: false,
        planPermission: "ASSETS",
        activePaths: new Set(["/assets", "/asset"]),
      });
    checkPermissions(["READ_OFFSET_LIST"], rbacPermissions) &&
      facilityNavItems.push({
        listItem: "Offsets",
        path: "/offsets",
        icon: OffsetIcon,
        children: [],
        isOpen: false,
        planPermission: "OFFSETS",
        activePaths: new Set(["/offsets", "/offset"]),
      });
    if (
      checkPermissions(["READ_CUSTOMER_LIST"], rbacPermissions) ||
      checkPermissions(["READ_SUPPLIER_LIST"], rbacPermissions)
    ) {
      let partners: any = {
        listItem: "Partners",
        path: "/partners",
        icon: PartnersIcon,
        children: [],
        isOpen: false,
        activePaths: new Set(["/partners"]),
      };
      if (checkPermissions(["READ_SUPPLIER_LIST"], rbacPermissions)) {
        partners.children.push({
          name: "Suppliers / Borrowers",
          path: "/suppliers",
          planPermission: "SRM",
          activePaths: new Set(["/partners/suppliers/list"]),
        });
      }
      if (checkPermissions(["READ_CUSTOMER_LIST"], rbacPermissions)) {
        partners.children.push({
          name: "Customers / Lenders",
          path: "/customers",
          planPermission: "",
          activePaths: new Set(["/partners/customers/list"]),
        });
      }
      facilityNavItems.push(partners);
    }
    return facilityNavItems;
  };
  const getBottomNavItems = () => {
    const bottomNavItems = [];
    bottomNavItems.push({
      listItem: "AI Assistant",
      path:
        greenMirrorUrl +
        "?token=" +
        localStorage.getItem("token") +
        "&userId=" +
        JSON.parse(localStorage.getItem("user") || "{}").id,
      icon: GMLogo,
      children: [],
      isOpen: false,
      planPermission: "DASHBOARD",
      activePaths: new Set([greenMirrorUrl]),
    });
    checkPermissions(["UPDATE_SETTINGS_INFO"], rbacPermissions) &&
      bottomNavItems.push({
        listItem: "Settings",
        path: "/settings/organisationInformation",
        icon: SettingsIcon,
        children: [],
        isOpen: false,
        activePaths: new Set(["/settings/organisationInformation"]),
      });
    bottomNavItems.push({
      listItem: "Resource centre",
      path: "/resourcecentre",
      icon: HelpIcon,
      children: [],
      isOpen: false,
      planPermission: "DASHBOARD",
      activePaths: new Set(["/resourcecentre"]),
    });
    return bottomNavItems;
  };
  // If adding any items in this topNavData list please make sure to change Index of topNavDat accrodingly in useEffect
  var [topNavData, setTopNavData] = React.useState<any>([
    {
      header: " ",
      ListItems: getHomeNavItems(),
    },
    {
      header: "ANALYTICS",
      ListItems: getAnalyticsNavItems(),
    },
    {
      header: "COMPANY",
      ListItems: getCompanyNavItems(),
    },
    {
      header: "FACILITY",
      ListItems: getFacilityNavItems(),
    },
  ]);

  var [orgSettingNavData, setOrgSettingNavData] = useState<any[]>([]);
  const setSettingsNav = () => {
    let settingData = [
      {
        listItem: "Organisation Information",
        path: "/settings/organisationInformation",
        isOpen: false,
        children: [],
        activePaths: new Set(["/settings/organisationInformation"]),
      },
      {
        listItem: "Organisation Preferences",
        path: "/settings/organisationpreference",
        isOpen: false,
        children: [],
        activePaths: new Set(["/settings/organisationpreference"]),
      },
    ];
    if (checkPermissions(["UPDATE_SUBSCRIPTION_PLAN"], rbacPermissions)) {
      settingData.push({
        listItem: "Subscription",
        path: "/settings/subscription",
        isOpen: false,
        children: [],
        activePaths: new Set(["/settings/subscription"]),
      });
    }
    settingData.push({
      listItem: "Security",
      path: "/settings/security",
      isOpen: false,
      children: [],
      activePaths: new Set(["/settings/security"]),
    });
    setOrgSettingNavData(settingData);
  };
  var [bottomNavData, setBottomNavData] = React.useState([
    {
      header: "",
      ListItems: getBottomNavItems(),
    },
  ]);

  // Handle user actions
  const toggleSideNavExpand = () => {
    setSideNavExpand(!sideNavExpand);
  };
  const handleItemExpand = (moduleIndex: any, navItemIndex: any) => {
    topNavData.map((eachModule: any, eachModuleIndex: any) => {
      eachModule.ListItems.map((eachNavItem: any, eachNavItemIndex: any) => {
        if (
          !(
            eachModuleIndex === moduleIndex && eachNavItemIndex === navItemIndex
          )
        ) {
          eachNavItem.isOpen = false;
        }
      });
    });
    topNavData[moduleIndex].ListItems[navItemIndex].isOpen =
      !topNavData[moduleIndex].ListItems[navItemIndex].isOpen;
    topNavData = [...topNavData];
    setSideNavExpand(true);
    setTopNavData(topNavData);
  };
  const handleNavItemClick = (
    moduleIndex: any,
    navItemIndex: any,
    each?: any
  ) => {
    setSideNavExpand(true);
    // handleItemExpand(moduleIndex, navItemIndex);
    if (each?.listItem === "Settings") {
      setOpenSettings(true);
      handleSettingItemClick(0, true);
    }
  };
  const handleSettingItemClick = (index: any, onSettingsClick: boolean) => {
    const prevSettingsNavData = orgSettingNavData.map((each: any) => ({
      ...each,
      isOpen: false,
    }));
    prevSettingsNavData[index].isOpen = !prevSettingsNavData[index].isOpen;
    setOrgSettingNavData([...prevSettingsNavData]);
    const selectedUrl = prevSettingsNavData[index].path;
    !onSettingsClick && navigate(selectedUrl);
  };

  const handleLinkClick = (navItem: any) => {
    if (
      !checkSubscriptionPermission(navItem.planPermission, planPermissions, -1)
    ) {
      setOpenUpgradeDialog(true);
    }
  };

  const handleUpgradeDialogClose = () => {
    setOpenUpgradeDialog(false);
  };

  // useEffects
  useEffect(() => {
    topNavData[1].ListItems = getAnalyticsNavItems();
    topNavData[2].ListItems = getCompanyNavItems();
    topNavData[3].ListItems = getFacilityNavItems();
    setTopNavData(topNavData);
    bottomNavData[0].ListItems = getBottomNavItems();
    setBottomNavData(bottomNavData);
    setSettingsNav();
  }, [rbacPermissions]);

  useEffect(() => {
    setShowUpgradeButton(subscriptionApiCompleted);
  }, [subscriptionApiCompleted]);

  // Sidenav list tsx
  const topNavItems = topNavData.map((module: any, moduleIndex: any) => (
    <List
      key={module.header}
      className="text-neutral-400 bg-neutral-900"
      component="nav"
      aria-labelledby="nested-list-subheader"
      subheader={
        sideNavExpand && (
          <ListSubheader
            component="div"
            id="nested-list-subheader"
            sx={{
              backgroundColor: theme.palette.grey[900],
              color: theme.palette.grey[600],
              paddingLeft: "1.5rem",
            }}
          >
            {module.header}
          </ListSubheader>
        )
      }
    >
      {module.ListItems.map((navItem: any, navItemIndex: any) => (
        <React.Fragment key={navItem.listItem}>
          {navItem.children.length ? (
            <ListItemButton
              className={`pading ${
                topNavData[moduleIndex].ListItems[navItemIndex].isOpen
                  ? "!bg-primary-900"
                  : ""
              } ${!sideNavExpand ? "!border-2 !rounded-2xl" : ""} ${
                "/" + window.location.pathname.split("/")[1] === navItem.path
                  ? "!bg-primary-900"
                  : undefined
              }`}
              sx={
                "/" + window.location.pathname.split("/")[1] === navItem.path
                  ? styles.navActive
                  : {}
              }
              onClick={() => handleItemExpand(moduleIndex, navItemIndex)}
            >
              <SvgIcon
                className={`pw-svg ${
                  topNavData[moduleIndex].ListItems[navItemIndex].isOpen &&
                  "pw-svg-fill"
                }`}
                inheritViewBox={true}
                component={navItem.icon}
              />
              {sideNavExpand && (
                <ListItemText
                  className={`${
                    "/" + window.location.pathname.split("/")[1] ===
                    navItem.path
                      ? "nav-active "
                      : undefined
                  } `}
                  primary={navItem.listItem}
                  id={navItem.path}
                />
              )}
              {navItem.listItem === "Partners" && sideNavExpand && (
                <div className="px-1.5 py-0.5 bg-blue-400 rounded-md border border-blue-400 justify-center items-center gap-2.5 flex mr-4">
                  <div className="justify-start items-center gap-0.5 flex">
                    <div className="text-indigo-50 text-xs font-medium font-['Proxima Nova']">
                      Beta
                    </div>
                  </div>
                </div>
              )}
              {sideNavExpand &&
                (navItem.children.length > 0 ? (
                  topNavData[moduleIndex].ListItems[navItemIndex].isOpen ? (
                    <RemoveIcon />
                  ) : (
                    <AddIcon />
                  )
                ) : (
                  ""
                ))}
            </ListItemButton>
          ) : (
            <Link
              to={
                checkSubscriptionPermission(
                  navItem.planPermission,
                  planPermissions,
                  -1
                )
                  ? navItem.path
                  : window.location.pathname
              }
              onClick={() => {
                handleLinkClick(navItem);
              }}
              className="pw-text-decoration-none text-neutral-400"
            >
              <ListItemButton
                onClick={() => {
                  handleItemExpand(moduleIndex, navItemIndex);
                  handleNavItemClick(moduleIndex, navItemIndex);
                }}
                sx={
                  navItem.activePaths?.has(window.location.pathname)
                    ? styles.navActive
                    : {}
                }
                className={`${
                  navItem.activePaths?.has(window.location.pathname)
                    ? "!bg-primary-900"
                    : undefined
                }`}
              >
                <SvgIcon
                  className={`pw-svg ${
                    navItem.activePaths?.has(window.location.pathname)
                      ? "pw-svg-fill"
                      : ""
                  }`}
                  inheritViewBox={true}
                  component={navItem.icon}
                />
                {sideNavExpand && (
                  <ListItemText
                    className={
                      navItem.activePaths?.has(window.location.pathname)
                        ? "nav-active"
                        : ""
                    }
                    id={navItem.path}
                    primary={navItem.listItem}
                  />
                )}
                {sideNavExpand &&
                  !checkSubscriptionPermission(
                    navItem.planPermission,
                    planPermissions,
                    -1
                  ) &&
                  showUpgradeButton && (
                    <SubscriptionUpgrade fromSideNav={true} />
                  )}
              </ListItemButton>
            </Link>
          )}

          <Collapse
            className="pw-sidenav-child-bg"
            in={
              topNavData[moduleIndex].ListItems[navItemIndex].isOpen &&
              sideNavExpand
            }
            timeout="auto"
            unmountOnExit
          >
            {navItem.children.map((eachItem: any) => (
              <List key={eachItem.name} component="div" disablePadding>
                <Link
                  to={
                    checkSubscriptionPermission(
                      eachItem.planPermission,
                      planPermissions,
                      -1
                    )
                      ? navItem.path + eachItem.path
                      : window.location.pathname
                  }
                  onClick={() => {
                    handleLinkClick(eachItem);
                  }}
                  className="pw-text-decoration-none text-neutral-200"
                >
                  <ListItemButton
                    onClick={() => {
                      handleNavItemClick(moduleIndex, navItemIndex);
                    }}
                  >
                    {sideNavExpand && (
                      <>
                        <ListItemText
                          className={
                            eachItem?.activePaths?.has(
                              window.location.pathname.replace(/\/\d+$/, "")
                            )
                              ? "nav-active-1"
                              : ""
                          }
                          id={navItem.path + eachItem.path}
                          primary={eachItem.name}
                        />
                      </>
                    )}
                    {sideNavExpand &&
                      !checkSubscriptionPermission(
                        eachItem.planPermission,
                        planPermissions,
                        -1
                      ) &&
                      showUpgradeButton && (
                        <SubscriptionUpgrade fromSideNav={true} />
                      )}
                  </ListItemButton>
                </Link>
              </List>
            ))}
          </Collapse>
        </React.Fragment>
      ))}
    </List>
  ));
  const settingNavItems = orgSettingNavData.map(
    (navItem: any, navItemIndex: any) => (
      <div
        onClick={() => handleSettingItemClick(navItemIndex, false)}
        className={`p-2 ${
          navItem.isOpen && "pw-bg-white pw-bg-black-side-nav pw-cursor-pointer"
        } `}
      >
        {navItem.listItem}
      </div>
    )
  );
  const bottomNavItems = bottomNavData.map((module, moduleIndex) => (
    <List
      key={module.header}
      className="text-neutral-400 bg-neutral-900"
      component="nav"
      aria-labelledby="nested-list-subheader"
    >
      <Divider
        sx={{
          borderColor: theme.palette.grey[700],
          paddingTop: "1rem",
        }}
      />
      {module.ListItems.map((navItem, navItemIndex) => (
        <React.Fragment key={navItem.listItem}>
          {!navItem.children.length ? (
            <Link
              to={
                checkSubscriptionPermission(
                  navItem.planPermission,
                  planPermissions,
                  -1
                )
                  ? navItem.path
                  : window.location.pathname
              }
              target={
                checkSubscriptionPermission(
                  navItem.planPermission,
                  planPermissions,
                  -1
                )
                  ? navItem.listItem === "Help" ||
                    navItem.listItem === "AI Assistant"
                    ? "_blank"
                    : ""
                  : ""
              }
              onClick={() => {
                handleLinkClick(navItem);
              }}
              className="pw-text-decoration-none text-neutral-400"
            >
              <ListItemButton
                onClick={() =>
                  handleNavItemClick(moduleIndex, navItemIndex, navItem)
                }
                sx={
                  navItem.activePaths?.has(window.location.pathname)
                    ? styles.navActive
                    : {}
                }
                className={`${
                  navItem.activePaths?.has(window.location.pathname)
                    ? "!bg-primary-900"
                    : undefined
                }`}
              >
                <SvgIcon
                  className={`pw-svg ${
                    navItem.activePaths?.has(window.location.pathname) &&
                    "pw-svg-fill"
                  }`}
                  inheritViewBox={true}
                  component={navItem.icon}
                />
                {sideNavExpand && (
                  <ListItemText
                    className={
                      navItem.activePaths?.has(window.location.pathname)
                        ? "nav-active"
                        : ""
                    }
                    id={navItem.path}
                    primary={navItem.listItem}
                  />
                )}
                {sideNavExpand &&
                  !checkSubscriptionPermission(
                    navItem.planPermission,
                    planPermissions,
                    -1
                  ) &&
                  showUpgradeButton && (
                    <SubscriptionUpgrade fromSideNav={true} />
                  )}
              </ListItemButton>
            </Link>
          ) : (
            ""
          )}
        </React.Fragment>
      ))}
    </List>
  ));

  return (
    <Fragment>
      <div className="flex flex-col pw-side-nav h-full">
        <img
          className={`cursor-pointer pw-comp-icon pw-bg-neutral-900 w-full ${
            !sideNavExpand && "pw-comp-icon-small"
          }`}
          src={
            sideNavExpand
              ? "assets/images/Planetwise-logo1.png"
              : "assets/images/Icons-Dark-mode.png"
          }
          alt=" "
          onClick={() => {
            navigate("/home");
          }}
        ></img>
        <div
          className={`flex flex-col relative pw-side-nav-content 
            ${
              sideNavExpand ? "pw-side-nav-width-expand" : "pw-side-nav-width"
            }`}
        >
          {!openSettings ? (
            <div className="flex flex-col !h-full">
              <div className="flex flex-col !w-full pw-top-nav-content">
                {sideNavExpand && !openSettings && <SubscriptionRemainder />}
                <div
                  className={`pw-nav-items flex flex-col ${
                    sideNavExpand
                      ? "px-2"
                      : "items-center mx-2 !overflow-y-hidden"
                  }`}
                >
                  {topNavItems}
                </div>
              </div>
              {userRole !== 7 ? (
                <div
                  className={`pw-nav-items flex flex-col w-full pw-bottom-nav-content !overflow-y-hidden ${
                    sideNavExpand ? "px-2" : "items-center "
                  }`}
                >
                  {bottomNavItems}
                </div>
              ) : (
                ""
              )}
              <div
                className={`pw-slide-icon ${
                  sideNavExpand && "pw-slide-icon-expand"
                }`}
                onClick={toggleSideNavExpand}
              >
                {sideNavExpand ? (
                  <KeyboardArrowLeftRoundedIcon className="text-neutral-500" />
                ) : (
                  <KeyboardArrowRightRoundedIcon className="text-neutral-500" />
                )}
              </div>
            </div>
          ) : (
            <div
              className={`pw-nav-items ${
                sideNavExpand
                  ? "px-2"
                  : "flex flex-col items-center mx-2 !overflow-y-hidden"
              }`}
            >
              <div className="text-white">
                <div className="flex gap-x-6 items-center">
                  <div onClick={() => setOpenSettings(false)}>
                    <img
                      src="assets/icons/keyboard-backspace.svg"
                      alt=""
                      className="pw-cursor-pointer"
                    />
                  </div>
                  <div
                    onClick={() => setOpenSettings(false)}
                    className="pw-font-14 cursor-pointer"
                  >
                    Settings
                  </div>
                </div>

                <div className="pw-text-neutral-400 pw-font-14 font-medium mt-5 pw-cursor-pointer">
                  {settingNavItems}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      <UpgradeMessageDialog
        open={openUpgradeDialog}
        onClose={handleUpgradeDialogClose}
      />
    </Fragment>
  );
};

export default SideNav;
